// DOM bar waveform — deterministic musical peak shapes, ported from the
// Teamix design system. Each [data-waveform] element is filled with spans;
// the played/unplayed split follows data-progress (RTL: fills from the right
// via the flex direction set in CSS).
//
// data attributes:
//   data-kind           mix | vocals | drums | bass | melody
//   data-seed           deterministic seed
//   data-bars           bar count
//   data-progress       0..1 played fraction
//   data-color          unplayed bar color (optional; default var(--ink-600))
//   data-color-opacity  wrap data-color in color-mix() at this alpha
//   data-played-color   played bar color

type PeakKind = 'mix' | 'vocals' | 'drums' | 'bass' | 'melody';

function peaks(n: number, kind: PeakKind, seed: number): number[] {
  let s = seed * 7919 + 13;
  const rnd = (): number => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / n;
    let v = 0.5;
    if (kind === 'mix') {
      const swell = 0.35 + 0.65 * Math.pow(Math.sin(Math.PI * Math.min(t * 1.15, 1)), 0.6);
      const phrase = 0.8 + 0.2 * Math.sin(i * 1.9 + seed);
      v = swell * phrase * (0.85 + 0.3 * rnd());
      if (t > 0.52 && t < 0.58) v *= 0.3; // the drop
    } else if (kind === 'vocals') {
      const active = Math.sin(t * Math.PI * 5 + 0.4) > -0.15;
      v = active ? 0.4 + 0.4 * Math.abs(Math.sin(i * 0.9)) + 0.18 * rnd() : 0.07 + 0.05 * rnd();
    } else if (kind === 'drums') {
      v = i % 4 === 0 ? 0.92 + 0.08 * rnd() : i % 2 === 0 ? 0.55 + 0.12 * rnd() : 0.3 + 0.1 * rnd();
      if (t > 0.44 && t < 0.52) v *= 0.22;
    } else if (kind === 'bass') {
      v = Math.floor(i / 6) % 2 === 0 ? 0.68 + 0.12 * rnd() : 0.42 + 0.1 * rnd();
      if (t > 0.44 && t < 0.5) v *= 0.4;
    } else if (kind === 'melody') {
      v = 0.5 + 0.28 * Math.sin(i * 0.55 + 1) + 0.14 * Math.sin(i * 1.7) + 0.08 * rnd();
    }
    out.push(Math.max(0.05, Math.min(1, v)));
  }
  return out;
}

function render(el: HTMLElement): void {
  const kind = (el.dataset.kind || 'mix') as PeakKind;
  const seed = parseFloat(el.dataset.seed || '7');
  const bars = parseInt(el.dataset.bars || '96', 10);
  const progress = parseFloat(el.dataset.progress || '0');
  const played = el.dataset.playedColor || 'var(--accent)';

  let base = el.dataset.color || 'var(--ink-600)';
  const alpha = el.dataset.colorOpacity;
  if (alpha) {
    base = `color-mix(in srgb, ${base} ${Math.round(parseFloat(alpha) * 100)}%, transparent)`;
  }

  const data = peaks(bars, kind, seed);
  const frag = document.createDocumentFragment();
  data.forEach((h, i) => {
    const bar = document.createElement('span');
    bar.style.height = `${Math.round(h * 100)}%`;
    bar.style.background = i / data.length < progress ? played : base;
    frag.appendChild(bar);
  });
  el.replaceChildren(frag);
}

export function initWaveforms(): void {
  document.querySelectorAll<HTMLElement>('[data-waveform]').forEach(render);
}
